import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Check, ArrowLeft, Zap, Video, RefreshCw, Target, Share2, Gift, X, Clock, Trophy } from "lucide-react";
import CheckoutModal from "@/components/CheckoutModal";
import ShareModal from "@/components/ShareModal";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/9fe16284-c883-46c5-9976-fb4189ef86a2/images/4414b575521af6b937b0721d2c61060a2fec8b4fac41d65ab6fa293761654c61.png";

export default function PricingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(searchParams.get("tab") || "subscription");
  const [checkoutModal, setCheckoutModal] = useState(null); // {type, id, name, price}
  const [highlightedItem, setHighlightedItem] = useState(null);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Currency localization
  const [currencySettings, setCurrencySettings] = useState({
    user_currency: "CAD",
    plan_prices: null
  });
  
  const [giftForm, setGiftForm] = useState({ 
    senderName: user?.name || "", 
    senderEmail: user?.email || "",
    recipientEmail: "", 
    recipientName: "", 
    message: "", 
    selectedPack: null 
  });
  
  // Update gift form when user changes
  useEffect(() => {
    if (user) {
      setGiftForm(f => ({
        ...f,
        senderName: user.name || f.senderName,
        senderEmail: user.email || f.senderEmail
      }));
    }
  }, [user]);
  
  // Refs for scrolling to sections
  const postCreditsRef = useRef(null);
  const videoCreditsRef = useRef(null);
  const regenCreditsRef = useRef(null);

  // Sync tab state with URL params and handle item/highlight params
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const itemParam = searchParams.get("item");
    const highlightParam = searchParams.get("highlight");
    const durationParam = searchParams.get("duration");
    const qualityParam = searchParams.get("quality");
    
    if (tabParam && (tabParam === "subscription" || tabParam === "payg")) {
      setSelectedTab(tabParam);
    }
    
    // Handle item param for direct navigation to specific credit type
    if (itemParam) {
      setHighlightedItem(itemParam);
      // Scroll to the appropriate section after a short delay
      setTimeout(() => {
        if (itemParam === "posts" && postCreditsRef.current) {
          postCreditsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (itemParam === "videos" && videoCreditsRef.current) {
          videoCreditsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (itemParam === "regen" && regenCreditsRef.current) {
          regenCreditsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 500);
      
      // Clear highlight after 3 seconds
      setTimeout(() => setHighlightedItem(null), 3000);
    }
    
    // Handle highlight param for video options
    if (highlightParam || (durationParam && qualityParam)) {
      const videoType = highlightParam || `${durationParam}_${qualityParam}`;
      setHighlightedItem(videoType);
      setTimeout(() => {
        if (videoCreditsRef.current) {
          videoCreditsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 500);
      setTimeout(() => setHighlightedItem(null), 3000);
    }
  }, [searchParams]);

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    setSearchParams({ tab });
  };

  useEffect(() => {
    fetchPricing();
    fetchCurrencySettings();
  }, [user]);

  const fetchPricing = async () => {
    try {
      const [plansRes, bundlesRes] = await Promise.all([
        apiClient.get("/settings/plans"),
        apiClient.get("/settings/bundles")
      ]);
      // Handle new plans response format with yearly_discount_percent
      const plansData = plansRes.data.plans || plansRes.data;
      setPlans(Array.isArray(plansData) ? plansData : []);
      setBundles(bundlesRes.data);
    } catch (error) {
      console.error("Pricing fetch error:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCurrencySettings = async () => {
    try {
      if (user) {
        // Logged-in user: use authenticated endpoint
        const res = await apiClient.get("/settings/currency");
        setCurrencySettings(res.data);
      } else {
        // Visitor: detect country and get public currency settings
        const { detectVisitorCountry } = await import("@/lib/geoUtils");
        const country = await detectVisitorCountry();
        const res = await apiClient.get(`/settings/currency-public?country=${country}`);
        setCurrencySettings({
          user_currency: res.data.local_currency,
          user_country: country,
          plan_prices: res.data.plan_prices
        });
      }
    } catch (error) {
      console.log("Currency settings not available, using defaults");
    }
  };
  
  // Helper to format price with CAD and optional local currency
  const formatPrice = (priceCAD, planId = null, isMonthly = true) => {
    const planPrices = currencySettings?.plan_prices?.[planId];
    if (planPrices) {
      const priceData = isMonthly ? planPrices.monthly : planPrices.yearly;
      if (priceData?.secondary) {
        return (
          <span>
            ${priceCAD} CAD
            <span className="text-xs text-viraly-muted ml-1">(~{priceData.secondary})</span>
          </span>
        );
      }
    }
    return `$${priceCAD} CAD`;
  };
  
  // Simple CAD format for inline display
  const formatCAD = (amount) => `$${amount} CAD`;

  const handleShare = async () => {
    // On mobile with native share support, use it
    if (navigator.share && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      try {
        await navigator.share({
          title: 'Viraly Pricing',
          text: 'Check out Viraly\'s affordable pricing for AI social media automation!',
          url: window.location.href
        });
        toast.success("Shared successfully!");
      } catch (error) {
        if (error.name !== 'AbortError') {
          setShowShareModal(true);
        }
      }
    } else {
      // On desktop, show our custom share modal
      setShowShareModal(true);
    }
  };

  const handleSubscribe = async (plan) => {
    if (!user) {
      navigate("/auth?mode=register");
      return;
    }
    
    setCheckoutModal({
      type: "subscription",
      id: plan.id,
      name: `${plan.name} Plan`,
      price: plan.price,
      validityDays: plan.validity_days || 30
    });
  };

  const handleBuyBundle = async (bundle) => {
    if (!user) {
      navigate("/auth?mode=register");
      return;
    }
    
    setCheckoutModal({
      type: bundle.type === "video_addon" ? "video_addon" : "bundle",
      id: bundle.id,
      name: bundle.name,
      price: bundle.price,
      validityDays: bundle.validity_days || 90
    });
  };

  const handlePaymentSuccess = async () => {
    setCheckoutModal(null);
    // Refresh user data to get updated credits
    if (refreshUser) {
      await refreshUser();
    }
    toast.success("Your account has been upgraded!");
    navigate("/dashboard");
  };

  // Features are configurable from backend
  const planFeatures = {
    trial: [
      "5 AI-generated posts",
      "1 brand profile",
      "No video generation",
      "All 7 platforms supported",
      "Brand Learning",
      "500 FREE templates",
      "1 competitor to monitor",
      "Email support"
    ],
    starter: [
      "50 AI-generated posts/month",
      "2 brand profiles",
      "No video generation",
      "All 7 platforms supported",
      "Brand Learning",
      "500 FREE templates",
      "Multi-Image Collage (up to 9 images)",
      "Viral Predictor + Post Analyzer",
      "3 competitors to monitor",
      "5 regen credits",
      "Email support"
    ],
    growth: [
      "100 AI-generated posts/month",
      "3 brand profiles",
      "4 video credits (720p quality)",
      "↳ 10s consumes 1 credit, 20s consumes 2 credits",
      "All 7 platforms supported",
      "Brand Learning",
      "500 FREE templates",
      "Multi-Image Collage (up to 9 images)",
      "Viral Predictor + Post Analyzer",
      "5 competitors to monitor",
      "10 regen credits",
      "Email support"
    ],
    pro: [
      "200 AI-generated posts/month",
      "5 brand profiles",
      "8 video credits (720p & 1080p HD)",
      "↳ 20s 720p: 2 credits | 20s 1080p: 4 credits",
      "All 7 platforms supported",
      "Brand Learning",
      "500 FREE templates",
      "Multi-Image Collage (up to 9 images)",
      "Viral Predictor + Post Analyzer",
      "10 competitors to monitor",
      "20 regen credits",
      "Priority support"
    ]
  };

  return (
    <div className="min-h-screen bg-viraly-dark">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass-card border-0 border-b border-white/5 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Viraly" className="h-10 w-10" />
            <span className="font-heading font-bold text-2xl gradient-text">Viraly</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost"
              onClick={handleShare}
              className="text-viraly-muted hover:text-white p-2"
            >
              <Share2 className="w-5 h-5" />
            </Button>
            {user ? (
              <Button onClick={() => navigate("/dashboard")} className="btn-secondary" data-testid="dashboard-btn">
                Dashboard
              </Button>
            ) : (
              <Button onClick={() => navigate("/auth")} className="btn-primary" data-testid="login-btn">
                Get Started
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="px-6 py-16 max-w-6xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-viraly-muted hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            Simple, <span className="gradient-text">transparent</span> pricing
          </h1>
          <p className="text-viraly-muted text-lg max-w-2xl mx-auto">
            Choose the plan that fits your needs. Start free, upgrade anytime.
          </p>
        </motion.div>

        {/* Loyalty Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-4 mb-8 flex items-center justify-center gap-4"
        >
          <Gift className="w-5 h-5 text-viraly-orange" />
          <p className="text-sm">
            <span className="text-viraly-orange font-medium">Loyalty Rewards:</span> Refer friends or reach spending milestones to unlock discounts!
          </p>
          <Link to="/#loyalty-rewards" className="text-viraly-orange text-sm hover:underline">
            Learn more
          </Link>
        </motion.div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-12">
          <div className="glass-card p-1 inline-flex">
            <button
              onClick={() => handleTabChange("subscription")}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                selectedTab === "subscription" 
                  ? "bg-viraly-gradient text-white" 
                  : "text-viraly-muted hover:text-white"
              }`}
              data-testid="tab-subscription"
            >
              Monthly Plans
            </button>
            <button
              onClick={() => handleTabChange("payg")}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                selectedTab === "payg" 
                  ? "bg-viraly-gradient text-white" 
                  : "text-viraly-muted hover:text-white"
              }`}
              data-testid="tab-payg"
              title="Pay As You Go - Purchase credits without a subscription"
            >
              Pay As You Go
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-viraly-orange border-t-transparent rounded-full animate-spin" />
          </div>
        ) : selectedTab === "subscription" ? (
          /* Subscription Plans */
          <>
            {/* Monthly Plans */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {plans.filter(p => p.interval !== "year").sort((a, b) => a.price - b.price).map((plan, i) => {
                const yearlyDiscount = plan.yearly_discount_percent || 35;
                const monthlyIfYearly = Math.round(plan.price * (1 - yearlyDiscount / 100));
                const yearlySavings = Math.round((plan.price * 12) - (monthlyIfYearly * 12));
                
                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.1 }}
                    className={`card-base p-6 relative overflow-visible ${
                      plan.id === "growth" ? "ring-2 ring-viraly-orange mt-4" : ""
                    }`}
                    data-testid={`plan-${plan.id}`}
                  >
                    {plan.id === "growth" && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-viraly-gradient rounded-full text-sm font-bold whitespace-nowrap z-10">
                        Most Popular
                      </div>
                    )}
                    
                    <div className="text-center mb-6">
                      <h3 className="font-heading text-2xl font-bold mb-2">{plan.name}</h3>
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-heading font-bold gradient-text">${plan.price}</span>
                          <span className="text-viraly-muted">CAD/month</span>
                        </div>
                        {/* Show local currency if available */}
                        {currencySettings?.plan_prices?.[plan.id]?.monthly?.secondary && (
                          <span className="text-sm text-viraly-muted">
                            (~{currencySettings.plan_prices[plan.id].monthly.secondary})
                          </span>
                        )}
                      </div>
                      {/* Yearly savings callout */}
                      <div className="mt-2 p-2 rounded-lg bg-viraly-success/10 border border-viraly-success/20">
                        <p className="text-xs text-viraly-success font-medium">
                          With yearly: <span className="font-bold">${monthlyIfYearly} CAD/mo</span>
                          <span className="ml-1 text-viraly-muted">(Save ${yearlySavings} CAD/year!)</span>
                        </p>
                      </div>
                    </div>
                    
                    <ul className="space-y-3 mb-8">
                      {(planFeatures[plan.id] || []).map((feature, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-viraly-success flex-shrink-0 mt-0.5" />
                          <span className="text-viraly-light">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button 
                      onClick={() => handleSubscribe(plan)}
                      className={`w-full py-3 ${plan.id === "growth" ? "btn-primary" : "btn-secondary"}`}
                      data-testid={`subscribe-${plan.id}`}
                    >
                      {!user ? "Register to Use" : user?.plan === plan.id ? "Current Plan" : "Get Started"}
                    </Button>
                  </motion.div>
                );
              })}
            </div>

            {/* Yearly Plans Section */}
            <div className="mb-8">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="h-px bg-white/10 flex-1" />
                <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                  <Gift className="w-5 h-5 text-viraly-success" />
                  Yearly Plans - Save up to 35%
                </h3>
                <div className="h-px bg-white/10 flex-1" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.filter(p => p.interval !== "year").sort((a, b) => a.price - b.price).map((plan, i) => {
                  const yearlyDiscount = plan.yearly_discount_percent || 35;
                  const monthlyIfYearly = Math.round(plan.price * (1 - yearlyDiscount / 100));
                  const yearlyTotal = monthlyIfYearly * 12;
                  const regularYearlyTotal = plan.price * 12;
                  const yearlySavings = regularYearlyTotal - yearlyTotal;
                  
                  return (
                    <motion.div
                      key={`${plan.id}-yearly`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.1 }}
                      className="card-base p-6 bg-gradient-to-br from-viraly-success/5 to-emerald-500/5 border-viraly-success/20"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-heading text-xl font-bold">{plan.name} (Yearly)</h4>
                        <span className="px-2 py-1 text-xs bg-viraly-success/20 text-viraly-success rounded-full font-medium">
                          Save ${yearlySavings} CAD
                        </span>
                      </div>
                      
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-3xl font-heading font-bold text-viraly-success">${yearlyTotal}</span>
                        <span className="text-viraly-muted">CAD/year</span>
                      </div>
                      <p className="text-sm text-viraly-muted mb-4">
                        <span className="line-through">${regularYearlyTotal} CAD/year</span>
                        <span className="ml-2 text-viraly-success">→ ${monthlyIfYearly} CAD/mo effective</span>
                      </p>
                      
                      <Button 
                        onClick={() => handleSubscribe({...plan, id: `${plan.id}_yearly`, name: `${plan.name} (Yearly)`, price: yearlyTotal, interval: 'year'})}
                        className="w-full py-2.5 bg-viraly-success/20 hover:bg-viraly-success/30 border border-viraly-success/30 text-viraly-success"
                      >
                        Get Yearly Plan
                      </Button>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          /* PAYG Bundles */
          <div className="space-y-12">
            {/* Post Bundles */}
            <div ref={postCreditsRef}>
              <h3 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2">
                <Zap className="w-6 h-6 text-viraly-orange" />
                Post Bundles
              </h3>
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-500 ${highlightedItem === 'posts' ? 'ring-2 ring-viraly-orange ring-offset-4 ring-offset-viraly-dark rounded-2xl p-2' : ''}`}>
                {bundles.filter(b => b.type === "payg").map((bundle, i) => (
                  <motion.div
                    key={bundle.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.1 }}
                    className="card-base p-6"
                    data-testid={`bundle-${bundle.id}`}
                  >
                    <h4 className="font-heading text-xl font-bold mb-2">{bundle.name}</h4>
                    <p className="text-3xl font-heading font-bold gradient-text mb-2">
                      {bundle.posts} posts
                    </p>
                    <p className="text-viraly-muted mb-6">${bundle.price} CAD</p>
                    <Button 
                      onClick={() => handleBuyBundle(bundle)}
                      className="btn-secondary w-full"
                    >
                      {!user ? "Register to Use" : "Buy Now"}
                    </Button>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Video Credit Packs - For 10s/20s Videos */}
            <div ref={videoCreditsRef}>
              <h3 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2">
                <Video className="w-6 h-6 text-purple-500" />
                Video Credit Packs
                <span className="text-sm font-normal text-viraly-orange ml-2">(10s & 20s videos only)</span>
              </h3>
              <div className="p-3 rounded-xl bg-purple-500/10 border border-purple-500/20 mb-6">
                <p className="text-sm text-purple-300">
                  <strong>For Growth & Pro subscribers only.</strong> These credits extend your 10s/20s video capacity.
                  Credits work with your plan's quality tier — Growth uses 720p, Pro can choose 720p or 1080p HD.
                </p>
              </div>
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-500 ${highlightedItem === 'videos' || highlightedItem?.includes('_') ? 'ring-2 ring-purple-500 ring-offset-4 ring-offset-viraly-dark rounded-2xl p-2' : ''}`}>
                {bundles.filter(b => b.type === "video_addon").map((bundle, i) => (
                  <motion.div
                    key={bundle.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.1 }}
                    className="card-base p-6"
                    data-testid={`bundle-${bundle.id}`}
                  >
                    <h4 className="font-heading text-xl font-bold mb-2">{bundle.name}</h4>
                    <p className="text-3xl font-heading font-bold text-purple-400 mb-1">
                      {bundle.video_credits || bundle.videos} credits
                    </p>
                    {bundle.description && (
                      <p className="text-xs text-viraly-muted mb-2">{bundle.description}</p>
                    )}
                    <p className="text-viraly-light font-medium mb-4">${bundle.price} CAD</p>
                    <Button 
                      onClick={() => handleBuyBundle({...bundle, type: "video_addon"})}
                      className="btn-secondary w-full"
                    >
                      {!user ? "Register to Use" : "Buy Now"}
                    </Button>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Regen Packs */}
            <div ref={regenCreditsRef}>
              <h3 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2">
                <RefreshCw className="w-6 h-6 text-blue-500" />
                Regeneration Credits
              </h3>
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-500 ${highlightedItem === 'regen' ? 'ring-2 ring-blue-500 ring-offset-4 ring-offset-viraly-dark rounded-2xl p-2' : ''}`}>
                {bundles.filter(b => b.type === "regen_pack").map((bundle, i) => (
                  <motion.div
                    key={bundle.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.1 }}
                    className="card-base p-6"
                    data-testid={`bundle-${bundle.id}`}
                  >
                    <h4 className="font-heading text-xl font-bold mb-2">{bundle.name}</h4>
                    <p className="text-3xl font-heading font-bold text-blue-400 mb-2">
                      {bundle.credits} regenerations
                    </p>
                    <p className="text-viraly-muted mb-6">${bundle.price} CAD</p>
                    <Button 
                      onClick={() => handleBuyBundle(bundle)}
                      className="btn-secondary w-full"
                    >
                      {!user ? "Register to Use" : "Buy Now"}
                    </Button>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Extended Video Packs (30s-60s) */}
            <div>
              <h3 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2">
                <Clock className="w-6 h-6 text-emerald-500" />
                Extended Videos (30s - 60s)
              </h3>
              <p className="text-viraly-muted text-sm mb-6">
                Create longer videos beyond your plan's included credits. Available to Growth and Pro customers.
              </p>
              
              {/* 720p Extended */}
              <div className="mb-8">
                <h4 className="font-heading text-lg font-semibold mb-4 text-viraly-light flex items-center gap-2">
                  <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded">720p</span>
                  Standard Quality
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {bundles.filter(b => b.type === "extended_video" && b.quality === "720p").sort((a,b) => a.duration - b.duration).map((bundle, i) => (
                    <motion.div
                      key={bundle.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                      className="card-base p-4 text-center"
                      data-testid={`bundle-${bundle.id}`}
                    >
                      <p className="text-2xl font-heading font-bold text-emerald-400 mb-1">
                        {bundle.duration}s
                      </p>
                      <p className="text-xs text-viraly-muted mb-2">{bundle.description}</p>
                      <p className="text-lg font-bold text-viraly-light mb-3">${bundle.price} CAD</p>
                      <Button 
                        onClick={() => handleBuyBundle({...bundle, type: "extended_video"})}
                        className="btn-secondary w-full text-sm py-2"
                      >
                        {!user ? "Register" : "Buy"}
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              {/* 1080p Extended */}
              <div>
                <h4 className="font-heading text-lg font-semibold mb-4 text-viraly-light flex items-center gap-2">
                  <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded">1080p HD</span>
                  Premium Quality
                  <span className="text-xs text-viraly-muted">(Pro plan required)</span>
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {bundles.filter(b => b.type === "extended_video" && b.quality === "1080p").sort((a,b) => a.duration - b.duration).map((bundle, i) => (
                    <motion.div
                      key={bundle.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                      className="card-base p-4 text-center border-purple-500/20"
                      data-testid={`bundle-${bundle.id}`}
                    >
                      <p className="text-2xl font-heading font-bold text-purple-400 mb-1">
                        {bundle.duration}s
                      </p>
                      <p className="text-xs text-viraly-muted mb-2">{bundle.description}</p>
                      <p className="text-lg font-bold text-viraly-light mb-3">${bundle.price} CAD</p>
                      <Button 
                        onClick={() => handleBuyBundle({...bundle, type: "extended_video"})}
                        className="btn-secondary w-full text-sm py-2"
                      >
                        {!user ? "Register" : "Buy"}
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAQ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-20 text-center"
        >
          <h2 className="font-heading text-2xl font-bold mb-4">Still have questions?</h2>
          <p className="text-viraly-muted mb-6">
            Start your free trial with 5 posts. No credit card required.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button 
              onClick={() => navigate("/auth?mode=register")}
              className="btn-primary px-8"
              data-testid="start-free-btn"
            >
              Start Free Trial
            </Button>
            <Button 
              type="button"
              variant="ghost"
              onClick={() => setShowGiftModal(true)}
              className="btn-secondary px-6"
              data-testid="gift-pack-btn"
            >
              <Gift className="w-4 h-4 mr-2" />
              Gift a Pack
            </Button>
            <Button 
              type="button"
              variant="ghost"
              onClick={handleShare}
              className="btn-secondary px-6"
              data-testid={user ? "share-btn-bottom" : "refer-earn-btn"}
            >
              {user ? (
                <>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </>
              ) : (
                <>
                  <Trophy className="w-4 h-4 mr-2" />
                  Refer and Earn
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </div>

      {/* PayPal Checkout Modal */}
      <AnimatePresence>
        {checkoutModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setCheckoutModal(null)}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-md bg-viraly-card border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <h3 className="font-heading text-xl font-bold">Complete Purchase</h3>
                <button
                  onClick={() => setCheckoutModal(null)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors text-viraly-muted hover:text-white"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6">
                <CheckoutModal
                  productType={checkoutModal.type}
                  productId={checkoutModal.id}
                  baseAmount={checkoutModal.price}
                  productName={checkoutModal.name}
                  validityDays={checkoutModal.validityDays}
                  user={user}
                  onSuccess={handlePaymentSuccess}
                  onCancel={() => setCheckoutModal(null)}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gift a Pack Modal */}
      <AnimatePresence>
        {showGiftModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
          >
            <div 
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setShowGiftModal(false)}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-lg bg-viraly-card border border-white/10 rounded-2xl shadow-2xl p-6 my-8"
            >
              <button
                onClick={() => setShowGiftModal(false)}
                className="absolute top-4 right-4 text-viraly-muted hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-viraly-orange to-pink-500 flex items-center justify-center">
                  <Gift className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-heading text-2xl font-bold">Gift a Pack</h3>
                <p className="text-viraly-muted mt-2">Send credits to a friend, colleague, or client</p>
              </div>

              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                {/* Sender Info - only show fields if not logged in */}
                {!user && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm text-viraly-muted mb-1">Your Name *</label>
                        <input
                          type="text"
                          value={giftForm.senderName}
                          onChange={(e) => setGiftForm(f => ({ ...f, senderName: e.target.value }))}
                          placeholder="Your name"
                          className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-viraly-muted focus:border-viraly-orange/50 focus:outline-none text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-viraly-muted mb-1">Your Email *</label>
                        <input
                          type="email"
                          value={giftForm.senderEmail}
                          onChange={(e) => setGiftForm(f => ({ ...f, senderEmail: e.target.value }))}
                          placeholder="your@email.com"
                          className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-viraly-muted focus:border-viraly-orange/50 focus:outline-none text-sm"
                        />
                      </div>
                    </div>
                    <div className="border-t border-white/5 my-3"></div>
                  </>
                )}
                
                {/* Logged in user info display */}
                {user && (
                  <div className="bg-white/5 rounded-xl p-3 mb-2">
                    <p className="text-xs text-viraly-muted">Sending as:</p>
                    <p className="text-sm font-medium">{user.name || user.email}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-viraly-muted mb-1">Recipient's Name *</label>
                    <input
                      type="text"
                      value={giftForm.recipientName}
                      onChange={(e) => setGiftForm(f => ({ ...f, recipientName: e.target.value }))}
                      placeholder="Friend's name"
                      className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-viraly-muted focus:border-viraly-orange/50 focus:outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-viraly-muted mb-1">Recipient's Email *</label>
                    <input
                      type="email"
                      value={giftForm.recipientEmail}
                      onChange={(e) => setGiftForm(f => ({ ...f, recipientEmail: e.target.value }))}
                      placeholder="friend@example.com"
                      className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-viraly-muted focus:border-viraly-orange/50 focus:outline-none text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-viraly-muted mb-2">Select a Pack *</label>
                  <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                    {/* Show subscription plans if on subscription tab, otherwise show PAYG bundles */}
                    {selectedTab === "subscription" ? (
                      plans.filter(p => p.is_active).map(plan => (
                        <label
                          key={plan.id}
                          className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                            giftForm.selectedPack === plan.id 
                              ? 'border-viraly-orange bg-viraly-orange/10' 
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="giftPack"
                              value={plan.id}
                              checked={giftForm.selectedPack === plan.id}
                              onChange={(e) => setGiftForm(f => ({ ...f, selectedPack: e.target.value, packType: "subscription" }))}
                              className="sr-only"
                            />
                            <div>
                              <p className="font-medium text-sm">{plan.name} Plan</p>
                              <p className="text-xs text-viraly-muted">{plan.posts} posts + {plan.video_credits || 0} video credits</p>
                            </div>
                          </div>
                          <span className="font-bold text-viraly-orange">${plan.price} CAD/mo</span>
                        </label>
                      ))
                    ) : (
                      <>
                        {/* Post Bundles */}
                        {bundles.filter(b => b.type === "payg").map(bundle => (
                          <label
                            key={bundle.id}
                            className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                              giftForm.selectedPack === bundle.id 
                                ? 'border-viraly-orange bg-viraly-orange/10' 
                                : 'border-white/10 hover:border-white/30'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="radio"
                                name="giftPack"
                                value={bundle.id}
                                checked={giftForm.selectedPack === bundle.id}
                                onChange={(e) => setGiftForm(f => ({ ...f, selectedPack: e.target.value, packType: "posts" }))}
                                className="sr-only"
                              />
                              <div>
                                <p className="font-medium text-sm">{bundle.name}</p>
                                <p className="text-xs text-viraly-muted">{bundle.posts} post credits</p>
                              </div>
                            </div>
                            <span className="font-bold text-viraly-orange">${bundle.price} CAD</span>
                          </label>
                        ))}
                        {/* Video Credit Bundles */}
                        {bundles.filter(b => b.type === "video_addon").map(bundle => (
                          <label
                            key={bundle.id}
                            className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                              giftForm.selectedPack === bundle.id 
                                ? 'border-purple-500 bg-purple-500/10' 
                                : 'border-white/10 hover:border-white/30'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="radio"
                                name="giftPack"
                                value={bundle.id}
                                checked={giftForm.selectedPack === bundle.id}
                                onChange={(e) => setGiftForm(f => ({ ...f, selectedPack: e.target.value, packType: "videos" }))}
                                className="sr-only"
                              />
                              <div>
                                <p className="font-medium text-sm">{bundle.name}</p>
                                <p className="text-xs text-viraly-muted">{bundle.video_credits || bundle.credits} video credits</p>
                              </div>
                            </div>
                            <span className="font-bold text-purple-400">${bundle.price} CAD</span>
                          </label>
                        ))}
                        {/* Regen Credit Bundles */}
                        {bundles.filter(b => b.type === "regen_pack").map(bundle => (
                          <label
                            key={bundle.id}
                            className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                              giftForm.selectedPack === bundle.id 
                                ? 'border-blue-500 bg-blue-500/10' 
                                : 'border-white/10 hover:border-white/30'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="radio"
                                name="giftPack"
                                value={bundle.id}
                                checked={giftForm.selectedPack === bundle.id}
                                onChange={(e) => setGiftForm(f => ({ ...f, selectedPack: e.target.value, packType: "regen" }))}
                                className="sr-only"
                              />
                              <div>
                                <p className="font-medium text-sm">{bundle.name}</p>
                                <p className="text-xs text-viraly-muted">{bundle.credits} regen credits</p>
                              </div>
                            </div>
                            <span className="font-bold text-blue-400">${bundle.price} CAD</span>
                          </label>
                        ))}
                      </>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-viraly-muted mb-1">Personal Message (optional)</label>
                  <textarea
                    value={giftForm.message}
                    onChange={(e) => setGiftForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Enjoy creating amazing content!"
                    rows={2}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-viraly-muted focus:border-viraly-orange/50 focus:outline-none resize-none text-sm"
                  />
                </div>
                
                <Button
                  onClick={async () => {
                    // Validate required fields
                    if (!user && (!giftForm.senderName || !giftForm.senderEmail)) {
                      toast.error("Please enter your name and email");
                      return;
                    }
                    if (!giftForm.recipientEmail || !giftForm.recipientName || !giftForm.selectedPack) {
                      toast.error("Please fill all required fields");
                      return;
                    }
                    
                    try {
                      const response = await apiClient.post("/payments/gift/create", {
                        sender_name: user?.name || giftForm.senderName,
                        sender_email: user?.email || giftForm.senderEmail,
                        recipient_email: giftForm.recipientEmail,
                        recipient_name: giftForm.recipientName,
                        pack_type: giftForm.packType || "posts",
                        pack_id: giftForm.selectedPack,
                        message: giftForm.message,
                        is_guest_sender: !user
                      });
                      toast.success("Gift created! Proceeding to payment...");
                      // TODO: Redirect to PayPal checkout for the gift
                      setShowGiftModal(false);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Failed to create gift");
                    }
                  }}
                  className="btn-primary w-full py-3"
                  disabled={!giftForm.selectedPack || !giftForm.recipientEmail || (!user && (!giftForm.senderName || !giftForm.senderEmail))}
                >
                  Continue to Payment
                </Button>
                
                <p className="text-xs text-center text-viraly-muted">
                  Gift codes are valid for 90 days. Non-refundable once purchased.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)}
        shareData={{
          title: "Viraly Pricing - AI Social Media Automation",
          text: "Check out Viraly's affordable pricing plans for AI-powered social media content creation!",
          url: window.location.href
        }}
      />
    </div>
  );
}
